import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useLiveSupportRequest } from "src/hooks/useLiveSupportRequest";
import Layout from "src/components/layout/dashboard/Layout";
import Card from "src/components/common/card/Card";
import CardHeader from "src/components/common/card/CardHeader";
import PageHeader from "src/components/common/PageHeader";
import Button from "src/components/ui/Button";
import TableV2 from "src/components/ui/TableV2";
import { Badge } from "src/components/ui/Badge";
import Modal from "src/components/ui/Modal";
import MailContent from "src/components/common/MailContent";
import PageSizeSelect from "src/components/ui/PageSizeSelect";
import Input from "src/components/ui/Input";

import { toast } from "react-toastify";
import { formatDateTime } from "src/utils/date-time";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as supportRequestActions from "src/redux/slices/supportRequestSlice";

const SupportRequestsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { list, detail, create, reply } = useAppSelector(
    (state) => state.supportRequest
  );
  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Modal state for viewing a support request in a conversation view
  const [openRequestModal, setOpenRequestModal] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(
    null
  );
  const [selectedRequestRow, setSelectedRequestRow] = useState<any>(null);

  // Raise-a-ticket modal state
  const [raiseModalOpen, setRaiseModalOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  useLiveSupportRequest(selectedRequestId, "tenant", reply.loading || detail.loading);

  const openTicket = (id: string, fallbackRow?: any) => {
    dispatch(supportRequestActions.resetSupportRequestDetail());
    setSelectedRequestId(id);
    setSelectedRequestRow(fallbackRow || null);
    setOpenRequestModal(true);
    dispatch(supportRequestActions.getSupportRequestByIdTenantRequest(id));
  };

  const handleOpenRequestModal = (row: any) => {
    openTicket(row._id || row.id, row);
  };

  const handleCloseRequestModal = () => {
    setOpenRequestModal(false);
    setSelectedRequestId(null);
    setSelectedRequestRow(null);
    dispatch(supportRequestActions.resetSupportRequestDetail());
  };

  const handleLimitChange = (value: number) => {
    setLimit(value);
    setCurrentPage(1);
  };

  const fetchList = (page = currentPage) =>
    dispatch(
      supportRequestActions.getSupportRequestsListTenantRequest({
        page,
        limit,
      })
    );

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, limit]);

  // Deep link from a notification: /app/support?ticketId=<id>
  useEffect(() => {
    const ticketId = searchParams.get("ticketId");
    if (ticketId) {
      openTicket(ticketId);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleReply = (msg: string) => {
    if (!selectedRequestId) return;
    dispatch(
      supportRequestActions.replySupportRequestTenantRequest({
        _id: selectedRequestId,
        message: msg,
      })
    );
  };

  useEffect(() => {
    if (reply.data) {
      dispatch(supportRequestActions.resetReplySupportRequest());
      fetchList();
    } else if (reply.error) {
      toast.error(`Error: ${reply.error}`);
      dispatch(supportRequestActions.resetReplySupportRequest());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reply.data, reply.error]);

  const handleRaiseRequest = () => {
    setSubject("");
    setMessage("");
    setRaiseModalOpen(true);
  };

  const handleSubmitRaise = () => {
    if (!message.trim()) {
      toast.error("Please describe your issue.");
      return;
    }
    dispatch(
      supportRequestActions.raiseSupportRequestRequest({
        subject: subject.trim() || undefined,
        message: message.trim(),
      })
    );
  };

  useEffect(() => {
    if (create.data) {
      toast.success("Support request raised. Our team will get back to you.");
      setRaiseModalOpen(false);
      dispatch(supportRequestActions.resetCreateSupportRequest());
      fetchList();
    } else if (create.error) {
      toast.error(`Error: ${create.error}`);
      dispatch(supportRequestActions.resetCreateSupportRequest());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [create.data, create.error]);

  const normalizeStatus = (status: string) =>
    status?.toString().trim().toLowerCase();

  const modalRequest = useMemo(
    () => (detail.data?._id === selectedRequestId ? detail.data : selectedRequestRow),
    [detail.data, selectedRequestRow, selectedRequestId]
  );

  return (
    <Layout
      type="tenant"
      meta={{
        title: "Support Requests | Invyntra",
        description: "Raise and track support requests with our team.",
        keywords: "support, requests, invyntra",
      }}
    >
      <div className="space-y-4">
        {/* Page Header */}
        <PageHeader
          title="Support Requests"
          breadcrumbData={[
            { label: "Home", path: "/app/dashboard" },
            { label: "Support", path: "/app/support" },
          ]}
        />

        <Card>
          <CardHeader
            title="Support Requests"
            description="Raise a ticket and track replies from our team"
            actions={
              <>
                <Button onClick={handleRaiseRequest}>Raise Request</Button>
                <PageSizeSelect value={limit} onChange={handleLimitChange} />
              </>
            }
          />
          <TableV2
            columns={[
              {
                key: "requestId",
                header: "Request ID",
                accessor: ({ key, row }) => (
                  <button
                    className="font-mono text-indigo-600 hover:underline"
                    onClick={() => handleOpenRequestModal(row)}
                  >
                    #{row[key]}
                  </button>
                ),
              },
              { key: "name", header: "Name" },
              { key: "email", header: "Email" },
              { key: "mobile", header: "Mobile" },
              {
                key: "message",
                header: "Message",
                accessor: ({ key, row }) => (
                  <div className="max-w-xs truncate">{row[key]}</div>
                ),
              },
              {
                key: "status",
                header: "Status",
                accessor: ({ key, row }) => {
                  const status = normalizeStatus(
                    row[key] || row?.requestStatus || row?.supportStatus || ""
                  );
                  return (
                    <Badge
                      text={status ? status.toUpperCase() : "OPEN"}
                      color={status === "closed" ? "green" : "yellow"}
                    />
                  );
                },
              },
              {
                key: "createdAt",
                header: "Created",
                accessor: ({ key, row }) => (
                  <div>{formatDateTime(row[key], "DD MMM YYYY")}</div>
                ),
              },
            ]}
            data={list?.data?.data}
            pagination={list?.data?.pagination}
            onPageChange={(page) => setCurrentPage(page)}
          />
          {openRequestModal && (
            <Modal
              open={openRequestModal}
              onClose={handleCloseRequestModal}
              title={`Support Request #${modalRequest?.requestId || ""}`}
              size="2xl"
              bodyClassName="flex min-h-0 flex-1 overflow-hidden"
            >
              <MailContent
                key={selectedRequestId}
                request={modalRequest}
                replyResult={reply.data}
                replyError={reply.error}
                onReply={handleReply}
                replying={reply.loading}
                viewerType="tenant"
              />
            </Modal>
          )}

          {raiseModalOpen && (
            <Modal
              open={raiseModalOpen}
              onClose={() => setRaiseModalOpen(false)}
              title="Raise a support request"
              size="md"
            >
              <div className="space-y-4">
                <Input
                  label="Subject"
                  placeholder="What's this about?"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  optional
                />
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Message
                  </label>
                  <textarea
                    className="w-full rounded-lg border border-gray-300 p-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                    rows={4}
                    placeholder="Describe your issue…"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setRaiseModalOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmitRaise}
                    isLoading={create.loading}
                    disabled={!message.trim() || create.loading}
                  >
                    Submit
                  </Button>
                </div>
              </div>
            </Modal>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default SupportRequestsPage;
